import { Button, Card, DatePicker, Tab } from '@alifd/next';
import { Cascader, Image, Table } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import useService from '@/lib/hooks/useService';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';

import ezconImg from '../../../../../public/ezcon.png';
import EditFormModal from './EditFormModal';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;

const DepositRegistrationList = () => {
  const user = useSelector((state) => state.user);

  const [deposits, updateDeposits, depositService] = useService({
    collectionName: 'deposit',
  });
  const [residentAccountNumbers] = useService({
    collectionName: 'residentAccountNumbers',
  });

  const [allowedFuncs, setAllowedFuncs] = useState([]);

  useEffect(() => {
    if (user.userGroup.key) {
      setAllowedFuncs(user.userGroup.key.checkedKeys);
    }
  }, [user]);

  const [tabStatus, setTabStatus] = useState('寄件');
  const [filterObj, setFilterObj] = useState({ type: tabStatus });

  // 編輯表單
  const [showEditFormModal, setShowEditFormModal] = useState(false);
  const [editType, setEditType] = useState('add');
  const [editItem, setEditItem] = useState();

  const householdNumbersForCascaderOptions = useMemo(() => {
    const householdNumbers = residentAccountNumbers.map(
      (resident) => resident.householdNumber
    );
    return generateHouseHoldNumberCascader(householdNumbers);
  }, [residentAccountNumbers]);

  const afterEdit = () => {
    setShowEditFormModal(false);
    setEditItem(null);
    updateDeposits();
  };

  const onCancel = () => {
    setShowEditFormModal(false);
  };

  const columnSorter = (a, b, dataIndex) => {
    if (!a[dataIndex] && !b[dataIndex]) {
      return 0;
    }
    if (typeof a[dataIndex] && typeof b[dataIndex] === 'number') {
      return a[dataIndex] - b[dataIndex];
    }

    return a[dataIndex].localeCompare(b[dataIndex]);
  };

  const generateAndSetFormItem = (item) => {
    const householdCascadeValue = item.household
      ? item.household.split('-')
      : '';
    const peopleHousehold = item.peopleHousehold
      ? item.peopleHousehold.split('-')
      : null;
    setEditItem({
      ...item,
      recipient: item.recipient === '' ? [] : item.recipient,
      household: householdCascadeValue,
      peopleHousehold,
    });
    setShowEditFormModal(true);
    setEditType('edit');
  };

  const deleteDeposit = useCallback(
    async (recordId) => {
      const result = await Swal.fire({
        title: `確定刪除此寄物資料?`,
        text: '刪除後將無法復原!!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#BEBEBE',
        confirmButtonText: '確定',
        cancelButtonText: '取消',
      });
      if (result.isConfirmed) {
        Swal.fire('刪除!', '此戶號資料已刪除', 'success');

        await depositService.remove(recordId);
        updateDeposits();
      }
    },
    [depositService, updateDeposits]
  );

  const requiredColumns = useMemo(
    () => [
      {
        title: '編號',
        dataIndex: 'plateNumber',
        fixed: 'left',
        sorter: (a, b) => columnSorter(a, b, 'plateNumber'),
      },
      {
        title: '建立時間',
        dataIndex: 'createdTime',
        sorter: (a, b) => columnSorter(a, b, 'createdTime'),
        render: (value) => {
          return <>{moment(value).format('YYYY-MM-DD HH:mm:ss')}</>;
        },
      },
      {
        title: `${tabStatus}人戶號`,
        dataIndex: 'household',
        sorter: (a, b) => columnSorter(a, b, 'household'),
      },
      {
        title: `${tabStatus}人`,
        dataIndex: 'recipient',
        sorter: (a, b) => columnSorter(a, b, 'recipient'),
      },
      {
        title: '物品名稱',
        dataIndex: 'itemName',
        sorter: (a, b) => columnSorter(a, b, 'itemName'),
      },
      {
        title: '備註',
        dataIndex: 'remarks',
        sorter: (a, b) => columnSorter(a, b, 'remarks'),
      },
      {
        title: '照片',
        dataIndex: 'itemPhoto',
        render: (value) => {
          return (
            <Image
              className={styles.visitorPhoto}
              src={value || ezconImg}
              alt="訪客照片"
            />
          );
        },
      },
      {
        title: '操作',
        dataIndex: 'actions',
        fixed: 'right',
        render: (_value, record) => {
          return (
            <div className={styles.btn}>
              <Button
                type="normal"
                onClick={() => generateAndSetFormItem(record)}
              >
                編輯
              </Button>
              <Button
                type="primary"
                warning
                onClick={() => {
                  deleteDeposit(record._id);
                }}
              >
                刪除
              </Button>
            </div>
          );
        },
      },
    ],
    [deleteDeposit, tabStatus]
  );

  const tableColumns = useMemo(() => {
    if (allowedFuncs.includes('送件服務') && tabStatus === '收件') {
      requiredColumns.splice(6, 0, {
        title: '機器人派送',
        dataIndex: 'robotDelivery',
        sorter: (a, b) => columnSorter(a, b, 'robotDelivery'),
        render: (value) => (value ? '是' : '否'),
      });
    }

    if (tabStatus === '收件') {
      requiredColumns.splice(2, 0, {
        title: '寄件人',
        dataIndex: 'peopleName',
        sorter: (a, b) => columnSorter(a, b, 'peopleName'),
      });
    }

    const isPeopleNameColumnExist = requiredColumns.some(
      (column) => column.dataIndex === 'peopleName'
    );

    if (tabStatus === '寄件' && isPeopleNameColumnExist === false) {
      requiredColumns.splice(4, 0, {
        title: '收件人',
        dataIndex: 'peopleName',
        sorter: (a, b) => columnSorter(a, b, 'peopleName'),
      });
    }

    return requiredColumns;
  }, [allowedFuncs, requiredColumns, tabStatus]);

  const depositsDataSource = useMemo(() => {
    let dataSource = deposits.filter((deposit) => deposit.type === tabStatus);

    if ('createdTime' in filterObj) {
      const {
        createdTime: { $gte, $lte },
      } = filterObj;
      dataSource = dataSource.filter((deposit) =>
        moment(deposit.createdTime).isBetween($gte, $lte)
      );
    }

    if ('household' in filterObj) {
      dataSource = dataSource.filter(
        (deposit) => deposit.household === filterObj.household.join('-')
      );
    }

    return dataSource;
  }, [deposits, filterObj, tabStatus]);

  return (
    <>
      <Button
        onClick={() => {
          setShowEditFormModal(true);
          setEditType('add');
          setEditItem(undefined);
        }}
      >
        登記
      </Button>
      <div style={{ display: 'flex', marginTop: 10, marginBottom: 10 }}>
        <div>
          戶號：
          <Cascader
            placeholder="請選擇戶號"
            className={styles.cascader}
            options={householdNumbersForCascaderOptions}
            onChange={async (value) => {
              let newFilter = { ...filterObj };
              if (value) {
                newFilter = {
                  ...newFilter,
                  household: value,
                };
              } else {
                delete newFilter.household;
              }
              setFilterObj(newFilter);
            }}
          />
        </div>
        <div style={{ marginLeft: 10 }}>
          時間：
          <RangePicker
            onChange={async (value) => {
              let newFilter = { ...filterObj };
              if (value.length === 2) {
                newFilter = {
                  ...newFilter,
                  createdTime: {
                    $gte: moment(value[0]).startOf('day').valueOf(),
                    $lte: moment(value[1]).endOf('day').valueOf(),
                  },
                };
              } else {
                delete newFilter.createdTime;
              }
              setFilterObj(newFilter);
            }}
          />
        </div>
      </div>

      <Tab onChange={(key) => setTabStatus(key)} style={{ marginBottom: 10 }}>
        <Tab.Item title="寄件登記" key="寄件" />
        <Tab.Item title="收件登記" key="收件" />
      </Tab>
      <Card free>
        <Card.Content>
          <Table
            dataSource={depositsDataSource}
            columns={tableColumns}
            scroll={{ x: 'max-content' }}
            rowKey="_id"
          />
        </Card.Content>
      </Card>
      <EditFormModal
        {...{
          open: showEditFormModal,
          editType,
          afterEdit,
          onCancel,
          editItem,
        }}
      />
    </>
  );
};

export default DepositRegistrationList;
